import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Upload, X, Send, Sparkles } from "lucide-react";
import Seo from "../components/Seo";

const PROJECT_TYPES_QUOTE = [
  "Door Hanger",
  "Family Name Sign",
  "Seasonal Decor",
  "Nursery Sign",
  "Business Logo Sign",
  "Memorial Piece",
  "Interchangeable Insert",
  "Custom Gift",
  "Layered Wall Decor",
  "Other Custom Piece",
];

export default function QuoteRequest() {
  const location = useLocation();
  const presetType = location.state?.category || (location.state?.inspiration ? "Other Custom Piece" : "");
  const presetDescription = location.state?.inspiration
    ? `Inspired by your gallery piece: "${location.state.inspiration}" (${location.state.category}). `
    : "";

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    project_type: presetType,
    size: "",
    colors_theme: "",
    deadline: "",
    budget_range: "",
    description: presetDescription,
    indoor_outdoor: "",
    personalization_text: "",
    notes: "",
  });
  const [inspirationFiles, setInspirationFiles] = useState([]); // {name, filename, preview}
  const [artworkFiles, setArtworkFiles] = useState([]); // {name, filename, preview}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const upd = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const getLiveQuoteValue = (field) => {
    if (field === "customer_name") return document.querySelector('input[data-testid="quote-name"]')?.value ?? form.customer_name ?? "";
    if (field === "customer_email") return document.querySelector('input[data-testid="quote-email"]')?.value ?? form.customer_email ?? "";
    if (field === "project_type") return document.querySelector('button[data-testid="quote-type"]')?.textContent?.trim() || form.project_type || "";
    if (field === "size") return document.querySelector('button[data-testid="quote-size"]')?.textContent?.trim() || form.size || "";
    if (field === "description") return document.querySelector('textarea[data-testid="quote-description"]')?.value ?? form.description ?? "";
    return form[field] ?? "";
  };

  const onFile = async (e, target) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`${API}/quotes/upload?kind=${target}`, { method: "POST", body: fd });
        if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
        const data = await res.json();
        const next = { name: f.name, filename: data.filename, preview: URL.createObjectURL(f) };
        if (target === "artwork") {
          setArtworkFiles((prev) => [...prev, next]);
        } else {
          setInspirationFiles((prev) => [...prev, next]);
        }
      }
      toast.success("Uploaded.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (target, filename) => {
    if (target === "artwork") {
      setArtworkFiles((prev) => prev.filter((f) => f.filename !== filename));
    } else {
      setInspirationFiles((prev) => prev.filter((f) => f.filename !== filename));
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    const trimmedForm = {
      ...form,
      customer_name: (getLiveQuoteValue("customer_name") || form.customer_name)?.trim() || "",
      customer_email: (getLiveQuoteValue("customer_email") || form.customer_email)?.trim() || "",
      project_type: (getLiveQuoteValue("project_type") || form.project_type)?.trim() || "",
      size: (getLiveQuoteValue("size") || form.size)?.trim() || "",
      description: (getLiveQuoteValue("description") || form.description)?.trim() || "",
    };

    if (!trimmedForm.customer_name || !trimmedForm.customer_email || !trimmedForm.project_type || !trimmedForm.size || !trimmedForm.description || trimmedForm.description.length < 10) {
      toast.error("Please fill in name, email, project type, size, and a description (10+ chars).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...trimmedForm,
        inspiration_filenames: inspirationFiles.map((f) => f.filename),
        artwork_filenames: artworkFiles.map((f) => f.filename),
      };
      if (!payload.indoor_outdoor) delete payload.indoor_outdoor;
      await api.post("/quotes", payload);
      if (typeof window !== "undefined") window.posthog?.capture?.("quote_request_submitted", { channel: "website" });
      toast.success("Quote request received! We'll follow up from beyondboardwoodwork@gmail.com.");
      setForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        project_type: "",
        size: "",
        colors_theme: "",
        deadline: "",
        budget_range: "",
        description: "",
        indoor_outdoor: "",
        personalization_text: "",
        notes: "",
      });
      setInspirationFiles([]);
      setArtworkFiles([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="quote-page">
      <Seo
        title="Custom Orders"
        description="Request a custom laser-cut wood piece from Beyond Board Woodworking. Share your idea, photos, sizes, and deadline, and we'll design and craft it for you."
        path="/quote"
      />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-12">
        <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">Custom Quote Request</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E6] leading-tight">Tell us about your project</h1>
        <div className="quote-idea-callout mt-6 flex items-start gap-3 rounded-2xl p-5">
          <Sparkles className="w-5 h-5 text-[#D8A34B] mt-0.5 shrink-0" />
          <p className="text-[#B8B7B2] leading-relaxed">
            <strong className="text-[#F5F0E6]">Have an idea but not sure how to explain it?</strong> Send what you have — even a rough idea, screenshot, sketch, or inspiration photo helps.
          </p>
        </div>
        <p className="mt-4 text-stone-400 text-sm">
          Complex designs, logos, portraits, sports themes, memorial pieces, and multi-layer signs may require extra design time before production.
        </p>
        <p className="mt-3 text-stone-400 text-sm">
          Most round signs are made in an 11.5 inch standard size. Larger custom sizes may be available by request depending on the design.
        </p>
      </section>

      <form onSubmit={submit} className="max-w-5xl mx-auto px-6 lg:px-10 pb-24 space-y-8" data-testid="quote-form">
        <div className="quote-natural-panel rounded-3xl p-8 lg:p-10 space-y-6">
          <h2 className="font-display text-2xl text-stone-100">About you</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FieldText label="Your name *" id="qname" value={form.customer_name} onChange={(v) => upd("customer_name", v)} testid="quote-name" />
            <FieldText label="Email *" id="qemail" type="email" value={form.customer_email} onChange={(v) => upd("customer_email", v)} testid="quote-email" />
            <FieldText label="Phone (optional)" id="qphone" value={form.customer_phone} onChange={(v) => upd("customer_phone", v)} testid="quote-phone" />
          </div>
        </div>

        <div className="quote-natural-panel rounded-3xl p-8 lg:p-10 space-y-6">
          <h2 className="font-display text-2xl text-stone-100">Project details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FieldSelect
              label="Project type *"
              testid="quote-type"
              value={form.project_type}
              onChange={(v) => upd("project_type", v)}
              options={PROJECT_TYPES_QUOTE.map((p) => ({ value: p, label: p }))}
            />
            <FieldSelect
              label="Size *"
              testid="quote-size"
              value={form.size}
              onChange={(v) => upd("size", v)}
              options={[
                { value: "11.5-standard-round-sign", label: "11.5” standard round sign" },
                { value: "small-custom-piece", label: "Small custom piece" },
                { value: "larger-custom-request", label: "Larger custom request" },
                { value: "not-sure-yet", label: "Not sure yet" },
              ]}
            />
            <FieldSelect
              label="Indoor or outdoor use?"
              testid="quote-indoor-outdoor"
              value={form.indoor_outdoor}
              onChange={(v) => upd("indoor_outdoor", v)}
              options={[
                { value: "indoor", label: "Indoor" },
                { value: "outdoor", label: "Outdoor" },
                { value: "both", label: "Both / not sure" },
              ]}
            />
            <FieldText label="Deadline (optional)" id="qdeadline" value={form.deadline} onChange={(v) => upd("deadline", v)} placeholder="e.g. by Christmas" testid="quote-deadline" />
            <FieldText label="Budget range (optional)" id="qbudget" value={form.budget_range} onChange={(v) => upd("budget_range", v)} placeholder="e.g. $150-$300" testid="quote-budget" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FieldText label="Personalization text" id="qpers" value={form.personalization_text} onChange={(v) => upd("personalization_text", v)} placeholder="names, dates, phrase..." testid="quote-pers" />
            <FieldText label="Colors or theme" id="qtheme" value={form.colors_theme} onChange={(v) => upd("colors_theme", v)} placeholder="neutral, rustic, floral, school colors..." testid="quote-theme" />
          </div>
          <Field label="Description of your idea *" testid="quote-description">
            <Textarea
              rows={6}
              value={form.description}
              onChange={(e) => upd("description", e.target.value)}
              data-testid="quote-description"
              placeholder="Tell us what you imagine — colors, vibe, who it's for, any text..."
              className="bg-zinc-950/60 border-stone-100/10 text-stone-100 focus-visible:ring-[#D8A34B]"
            />
          </Field>
        </div>

        <div className="quote-natural-panel rounded-3xl p-8 lg:p-10 space-y-6">
          <h2 className="font-display text-2xl text-stone-100">Uploads & notes</h2>
          <p className="text-stone-400 text-sm">Inspiration photos, sketches, screenshots, logos, and reference artwork all help us review your request.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <p className="text-stone-200 font-medium">Inspiration image upload</p>
              <label className="block border-2 border-dashed border-stone-100/15 hover:border-[#D8A34B]/50 rounded-2xl p-8 text-center cursor-pointer transition" data-testid="quote-upload-zone-inspiration">
                <Upload className="w-8 h-8 mx-auto text-[#D8A34B] mb-3" />
                <p className="text-stone-300">{uploading ? "Uploading…" : "Upload inspiration images"}</p>
                <p className="text-xs text-stone-500 mt-1">PNG, JPG, WEBP, GIF, SVG, or PDF</p>
                <input type="file" accept="image/*,.svg,.pdf" multiple className="hidden" onChange={(e) => onFile(e, "inspiration")} data-testid="quote-upload-input-inspiration" />
              </label>
              {inspirationFiles.length > 0 && (
                <UploadList files={inspirationFiles} onRemove={(filename) => removeFile("inspiration", filename)} />
              )}
            </div>
            <div className="space-y-4">
              <p className="text-stone-200 font-medium">Logo or artwork upload if needed</p>
              <label className="block border-2 border-dashed border-stone-100/15 hover:border-[#D8A34B]/50 rounded-2xl p-8 text-center cursor-pointer transition" data-testid="quote-upload-zone-artwork">
                <Upload className="w-8 h-8 mx-auto text-[#D8A34B] mb-3" />
                <p className="text-stone-300">{uploading ? "Uploading…" : "Upload logo or artwork files"}</p>
                <p className="text-xs text-stone-500 mt-1">PNG, JPG, WEBP, SVG, or PDF</p>
                <input type="file" accept="image/*,.svg,.pdf" multiple className="hidden" onChange={(e) => onFile(e, "artwork")} data-testid="quote-upload-input-artwork" />
              </label>
              {artworkFiles.length > 0 && (
                <UploadList files={artworkFiles} onRemove={(filename) => removeFile("artwork", filename)} />
              )}
            </div>
          </div>
          <Field label="Anything else?" testid="quote-notes">
            <Textarea
              rows={4}
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              className="bg-zinc-950/60 border-stone-100/10 text-stone-100 focus-visible:ring-[#D8A34B]"
            />
          </Field>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="quote-submit"
          className="quote-natural-button h-13 px-8 py-4 text-base"
        >
          <Send className="w-4 h-4 mr-2" /> {submitting ? "Sending…" : "Send to Beyond Board"}
        </Button>
      </form>
    </div>
  );
}

function UploadList({ files, onRemove }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {files.map((f) => (
        <div key={f.filename} className="relative aspect-square rounded-xl overflow-hidden border border-stone-100/10 bg-zinc-950/50">
          {f.preview ? (
            <img src={f.preview} alt={f.name} className="w-full h-full object-cover" />
          ) : null}
          <div className="absolute inset-x-0 bottom-0 bg-black/70 p-2 text-[11px] text-stone-200 truncate">{f.name}</div>
          <button
            type="button"
            onClick={() => onRemove(f.filename)}
            className="absolute top-2 right-2 bg-black/80 rounded-full p-1 text-stone-100 hover:bg-[#D8A34B]"
            aria-label="remove"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div className="space-y-2" data-testid={testid ? `${testid}-wrap` : undefined}>
      <Label className="text-stone-200">{label}</Label>
      {children}
    </div>
  );
}

function FieldText({ label, id, value, onChange, type = "text", placeholder, testid }) {
  return (
    <Field label={label}>
      <Input id={id} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-zinc-950/60 border-stone-100/10 text-stone-100 focus-visible:ring-[#D8A34B]" />
    </Field>
  );
}

function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-zinc-950/60 border-stone-100/10 text-stone-100">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-stone-100/10 text-stone-100">
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} className="focus:bg-[#D8A34B]/20 focus:text-[#F5F0E6]">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
